/**
 * FairwayBid – die Merkmale einer Anfrage, je Kategorie
 *
 * Bisher stand fast alles im Beschreibungstext: "drei elektrische Carts mit
 * Dach", "Ballautomat für draußen, Kartenzahlung". Für einen Menschen ist das
 * lesbar. Für alles andere ist es nichts.
 *
 * Drei Dinge scheitern an Fließtext:
 *
 *   Bündeln. Ein Bündel ist EINE Bestellung aus EINER Charge. Neun Carts sind
 *   nur dann neun Carts, wenn es neunmal dasselbe Fahrzeug ist. Drei Benziner,
 *   drei Lithium-Fahrzeuge und drei Gebrauchte sind drei Bestellungen mit drei
 *   Preisen – und der Anbieter merkt es erst, wenn er schon gerechnet hat.
 *   Ohne Merkmale kann der Pool das nicht unterscheiden und würde Anlagen
 *   zusammenstellen, die nicht zusammengehören.
 *
 *   Kalkulieren. "Antriebsart Benzin" ist eine andere Preisliste als "Elektro,
 *   Lithium". Steht es als Wort im Text, muss der Anbieter jede Anfrage
 *   öffnen und raten; steht es als Wert im Feld, sieht er es in der Liste.
 *
 *   Zuordnen. Ein Händler, der nur Akku-Technik führt, will Benziner gar
 *   nicht erst angeboten bekommen. Danach filtern lässt sich nur, was als
 *   Wert existiert.
 *
 * Deshalb je Kategorie ein paar wenige Auswahlfelder – und nur solche, die
 * eine Golfanlage ohne Datenblatt beantworten kann. Eine Anlage, die nach
 * Nennleistung in kW gefragt wird, bricht das Formular ab.
 *
 * Bindend oder frei
 * -----------------
 * Jedes Merkmal ist eins von beiden:
 *
 *   bindend  Muss innerhalb eines Bündels gleich sein, sonst ist es keine
 *            gemeinsame Bestellung. Diese Felder gruppieren den Pool und
 *            werden beim Anhängen übernommen.
 *   frei     Gehört zur Anfrage, darf sich aber je Anlage unterscheiden.
 *            Dach und Ladegerät am Cart sind so ein Fall – die Fahrzeuge
 *            kommen trotzdem aus einer Lieferung.
 *
 * Je mehr Felder bindend sind, desto seltener finden zwei Anlagen zusammen.
 * Deshalb ist bindend die Ausnahme und frei die Regel: gebunden wird nur, was
 * den Artikel wirklich zu einem anderen Artikel macht.
 *
 * Warum im Code und nicht in der Console
 * --------------------------------------
 * Erweiterte Daten sind bei Sharetribe schemalos – die Feldliste der Console
 * steuert nur die Eingabemaske. Hier stehen die Felder neben der Regel, die
 * sie bindet (bundles.js liest MERKMAL_MATCH_FIELDS direkt aus dieser Datei).
 * Zwei getrennte Listen wären genau die Art Fehler, die niemand bemerkt, bis
 * ein Bündel Äpfel mit Birnen zusammenrechnet.
 *
 * Trägt jemand später denselben Schlüssel in der Console ein, gewinnt die
 * Console – wie beim Mengenfeld auch.
 */

/**
 * Die Merkmale je Kategorie.
 *
 * @typedef {Object} Merkmal
 * @property {string} key      Schlüssel in den erweiterten Daten
 * @property {string} label    Beschriftung im Formular
 * @property {boolean} bindend Muss im Bündel gleich sein?
 * @property {boolean} [mehrfach] Mehrfachauswahl (nie bindend)
 * @property {string} [hilfe]  Satz unter dem Feld
 * @property {Array<[string,string]>} optionen [Wert, Beschriftung]
 */
export const MERKMALE = {
  'golf-carts': [
    {
      key: 'cart_antrieb',
      label: 'Antriebsart',
      bindend: true,
      hilfe:
        'Entscheidet über Preis, Ladeinfrastruktur und Folgekosten – und darüber, mit welchen Anlagen Sie zusammen bestellen können.',
      optionen: [
        ['elektro_lithium', 'Elektro, Lithium'],
        ['elektro_blei', 'Elektro, Blei-Säure'],
        ['benzin', 'Benzin'],
        ['egal', 'Noch offen – bitte beraten'],
      ],
    },
    {
      key: 'cart_sitze',
      label: 'Sitzplätze',
      bindend: true,
      optionen: [
        ['2', '2 Sitze'],
        ['4', '4 Sitze'],
        ['6', '6 Sitze'],
        ['transport', 'Transportfahrzeug (Pritsche)'],
      ],
    },
    {
      key: 'cart_zustand',
      label: 'Zustand',
      bindend: true,
      optionen: [
        ['neu', 'Neufahrzeug'],
        ['vorfuehr', 'Vorführfahrzeug'],
        ['gebraucht', 'Gebraucht'],
        ['egal', 'Neu oder gebraucht – beides willkommen'],
      ],
    },
    {
      key: 'cart_ausstattung',
      label: 'Ausstattung',
      bindend: false,
      mehrfach: true,
      hilfe:
        'Darf sich je Anlage unterscheiden: Die Fahrzeuge kommen trotzdem aus einer Lieferung.',
      optionen: [
        ['dach', 'Dach'],
        ['windschutz', 'Windschutzscheibe'],
        ['ladegeraet', 'Ladegerät'],
        ['beleuchtung', 'Beleuchtung'],
        ['strassenzulassung', 'Straßenzulassung'],
        ['gps', 'GPS oder Display'],
      ],
    },
  ],

  ballautomaten: [
    {
      key: 'automat_aufstellung',
      label: 'Aufstellort',
      bindend: true,
      hilfe: 'Ein Automat für draußen ist ein anderes Gerät als einer für die Halle.',
      optionen: [['aussen', 'Im Freien'], ['ueberdacht', 'Überdacht'], ['innen', 'Innen']],
    },
    {
      key: 'automat_bezahlsystem',
      label: 'Bezahlsystem',
      bindend: false,
      mehrfach: true,
      hilfe:
        'Darf sich je Anlage unterscheiden – das lässt sich bei der Bestellung pro Gerät festlegen.',
      optionen: [
        ['muenze', 'Münze'],
        ['token', 'Chip oder Token'],
        ['karte', 'Karte (EC oder Kredit)'],
        ['app', 'App oder QR-Code'],
        ['rfid', 'Mitgliedsausweis (RFID)'],
      ],
    },
  ],

  ballwaschmaschinen: [
    {
      key: 'waschmaschine_bauart',
      label: 'Bauart',
      bindend: true,
      optionen: [
        ['durchlauf', 'Durchlaufmaschine'],
        ['trommel', 'Trommelwäscher'],
        ['hand', 'Handgerät'],
      ],
    },
    {
      key: 'waschmaschine_antrieb',
      label: 'Antrieb',
      bindend: true,
      optionen: [['netz', 'Netzstrom'], ['akku', 'Akku'], ['hand', 'Ohne Motor, von Hand']],
    },
  ],

  ballsammler: [
    {
      key: 'sammler_bauart',
      label: 'Bauart',
      bindend: true,
      optionen: [
        ['handpicker', 'Handpicker'],
        ['anhaenger', 'Anhänger-Picker für Fahrzeug'],
        ['aufsitz', 'Aufsitz- oder Selbstfahrer'],
        ['roboter', 'Sammelroboter'],
      ],
    },
  ],

  'range-matten': [
    {
      key: 'matten_typ',
      label: 'Mattentyp',
      bindend: true,
      optionen: [
        ['teppich', 'Teppichmatte (Standard)'],
        ['fiber', 'Fiber- oder Bürstenmatte'],
        ['hybrid', 'Hybridmatte'],
      ],
    },
    {
      key: 'matten_format',
      label: 'Format',
      bindend: true,
      hilfe: 'Ein anderes Maß ist ein anderer Zuschnitt – und damit eine andere Bestellung.',
      optionen: [
        ['150x150', '150 × 150 cm'],
        ['150x200', '150 × 200 cm'],
        ['100x150', '100 × 150 cm'],
        ['sonder', 'Sondermaß (im Text beschreiben)'],
      ],
    },
    {
      key: 'matten_unterbau',
      label: 'Unterbau',
      bindend: false,
      optionen: [
        ['rahmen', 'Mit Rahmen'],
        ['gummi', 'Mit Gummiunterlage'],
        ['ohne', 'Ohne Unterbau'],
      ],
    },
  ],

  pflegetechnik: [
    {
      key: 'pflege_geraet',
      label: 'Gerätetyp',
      bindend: true,
      optionen: [
        ['maehroboter', 'Mähroboter'],
        ['spindel', 'Spindelmäher'],
        ['sichel', 'Sichelmäher'],
        ['aerifizierer', 'Aerifizierer'],
        ['verticutierer', 'Verticutierer'],
        ['bunker', 'Bunkerpflege'],
        ['sonstiges', 'Anderes Pflegegerät'],
      ],
    },
    {
      key: 'pflege_antrieb',
      label: 'Antrieb',
      bindend: true,
      optionen: [
        ['akku', 'Akku'],
        ['elektro', 'Netzstrom'],
        ['benzin', 'Benzin'],
        ['diesel', 'Diesel'],
        ['hybrid', 'Hybrid'],
        ['egal', 'Noch offen – bitte beraten'],
      ],
    },
    {
      key: 'pflege_einsatz',
      label: 'Einsatzbereich',
      bindend: false,
      mehrfach: true,
      optionen: [
        ['green', 'Grüns'],
        ['vorgruen', 'Vorgrüns'],
        ['fairway', 'Fairways'],
        ['abschlag', 'Abschläge'],
        ['rough', 'Semi-Rough und Rough'],
        ['bunker', 'Bunker'],
      ],
    },
  ],

  'netze-systeme': [
    {
      key: 'netz_art',
      label: 'Art der Anlage',
      bindend: true,
      optionen: [
        ['fangnetz', 'Ballfangnetz'],
        ['zaun', 'Ballfangzaun'],
        ['abtrennung', 'Abtrennnetz auf der Range'],
        ['mastanlage', 'Mastanlage komplett'],
        ['reparatur', 'Reparatur oder Austausch'],
      ],
    },
    {
      key: 'netz_hoehe',
      label: 'Höhe',
      bindend: true,
      optionen: [
        ['bis6', 'Bis 6 m'],
        ['6bis10', '6 bis 10 m'],
        ['ueber10', 'Über 10 m'],
        ['unklar', 'Noch offen – bitte vor Ort prüfen'],
      ],
    },
    {
      key: 'netz_montage',
      label: 'Montage',
      bindend: false,
      optionen: [['mit', 'Mit Montage'], ['ohne', 'Nur Lieferung'], ['offen', 'Beides anbieten']],
    },
  ],

  gebrauchtmaschinen: [
    {
      key: 'gebraucht_art',
      label: 'Maschinenart',
      bindend: false,
      optionen: [
        ['maeher', 'Mäher'],
        ['aerifizierer', 'Aerifizierer oder Verticutierer'],
        ['transport', 'Transportfahrzeug'],
        ['cart', 'Golf-Cart'],
        ['bunker', 'Bunkermaschine'],
        ['sonstiges', 'Andere Maschine'],
      ],
    },
    {
      key: 'gebraucht_baujahr',
      label: 'Baujahr frühestens',
      bindend: false,
      hilfe: 'Grenzt die Angebote ein, ohne ein bestimmtes Modell vorzugeben.',
      optionen: [
        ['ab2020', 'Ab 2020'],
        ['ab2015', 'Ab 2015'],
        ['ab2010', 'Ab 2010'],
        ['egal', 'Baujahr egal'],
      ],
    },
  ],

  ersatzteile: [
    {
      key: 'ersatzteil_bereich',
      label: 'Bereich',
      bindend: false,
      hilfe: 'Hersteller, Modell und Teilenummer gehören in die Beschreibung.',
      optionen: [
        ['maehwerk', 'Mähwerk und Messer'],
        ['motor', 'Motor und Antrieb'],
        ['elektrik', 'Elektrik und Akku'],
        ['hydraulik', 'Hydraulik'],
        ['raeder', 'Räder und Reifen'],
        ['verschleiss', 'Verschleißteile'],
        ['sonstiges', 'Anderes Teil'],
      ],
    },
  ],
};

/** Alle Merkmale einer Kategorie – leer, wo keine hinterlegt sind. */
export const merkmaleFuer = kategorie => MERKMALE[kategorie] || [];

/** Die bindenden Merkmale einer Kategorie, als Schlüssel. */
export const bindendeFelderFuer = kategorie =>
  merkmaleFuer(kategorie)
    .filter(m => m.bindend)
    .map(m => m.key);

/**
 * Kategorie → bindende Schlüssel, in der Form, die bundles.js erwartet.
 *
 * Nur Kategorien mit mindestens einem bindenden Merkmal stehen drin: Ein
 * leerer Eintrag würde dasselbe bedeuten wie kein Eintrag, aber so aussehen,
 * als hätte jemand etwas vergessen.
 */
export const MERKMAL_MATCH_FIELDS = Object.keys(MERKMALE).reduce((acc, kategorie) => {
  const keys = bindendeFelderFuer(kategorie);
  return keys.length > 0 ? { ...acc, [kategorie]: keys } : acc;
}, {});

/** Alle Merkmale aller Kategorien, flach – für Beschriftungen. */
const ALLE = Object.keys(MERKMALE).reduce((acc, k) => [...acc, ...MERKMALE[k]], []);

/** Die Definition zu einem Schlüssel, oder null. */
export const merkmalVon = key => ALLE.find(m => m.key === key) || null;

/** Die Beschriftung eines Merkmals – der Schlüssel, wenn es keins ist. */
export const merkmalLabel = key => merkmalVon(key)?.label || key;

/** Die Beschriftung eines Wertes – der rohe Wert, wenn er unbekannt ist. */
export const merkmalWertLabel = (key, wert) => {
  const m = merkmalVon(key);
  if (!m) return wert;
  const treffer = m.optionen.find(([w]) => w === wert);
  return treffer ? treffer[1] : wert;
};

/**
 * Ein Merkmal als Sharetribe-Feldkonfiguration.
 *
 * Mehrfachauswahl wird zu "multi-enum", einfache Auswahl zu "enum" – das ist
 * dieselbe Form, in der auch die Console ihre Felder liefert, damit Formular,
 * Filter und Anzeige nichts Neues lernen müssen.
 */
const alsFeld = (merkmal, kategorie) => ({
  key: merkmal.key,
  scope: 'public',
  schemaType: merkmal.mehrfach ? 'multi-enum' : 'enum',
  enumOptions: merkmal.optionen.map(([option, label]) => ({ option, label })),
  ...(merkmal.hilfe ? { helpText: merkmal.hilfe } : {}),
  showConfig: { label: merkmal.label, displayInProfile: true },
  saveConfig: {
    label: merkmal.label,
    placeholderMessage: 'Bitte wählen',
    isRequired: false,
  },
  filterConfig: { indexForSearch: true, label: merkmal.label },
  categoryConfig: { limitToCategoryIds: true, categoryIds: [kategorie] },
});

/** Alle Merkmale als Feldkonfigurationen. */
export const MERKMAL_FIELDS = Object.keys(MERKMALE).reduce(
  (acc, kategorie) => [...acc, ...MERKMALE[kategorie].map(m => alsFeld(m, kategorie))],
  []
);

/**
 * Hängt die Merkmalsfelder an die Feldliste an.
 *
 * Was in der Console schon unter demselben Schlüssel steht, bleibt, wie es
 * dort steht – dieselbe Regel wie beim Mengenfeld.
 */
export const mitMerkmalen = (listingFields = []) => {
  const vorhanden = new Set((Array.isArray(listingFields) ? listingFields : []).map(f => f?.key));
  return [
    ...(Array.isArray(listingFields) ? listingFields : []),
    ...MERKMAL_FIELDS.filter(f => !vorhanden.has(f.key)),
  ];
};
